import React from 'react';
import PropTypes from 'prop-types';
import { fromGlobalId } from 'graphql-relay';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import { Link } from 'react-router';

import Grid from '../../components/Grid';
import Button from '../../components/Button';
import DetailDisplayRow from '../../components/DetailDisplayRow';
import Image from '../../components/Image';
import Header from '../../components/Header';
import Icon from '../../components/Icon';
import messages from './messages';
import './styles.css';

export function getAddress(school) {
  const addrLine = get(school, 'address.field_hs_elna_address_line1', '');
  const city = get(school, 'address.field_hs_elna_city', '');
  let state = get(school, 'address.field_hs_elna_state_latest', '');
  const zip = get(school, 'address.field_hs_eln_zip', '');
  state = `${state} ${zip}`.trim();
  let addr = '';
  if (city && state) {
    addr = `${city}, ${state}`;
  } else {
    addr = city + state;
  }
  if (addrLine && addr) {
    addr = `${addrLine}\n${addr}`;
  }
  return addr;
}

export function getLoc(school) {
  const lat = get(school, 'address.field_hs_elna_location_latlon.latitude');
  const lon = get(school, 'address.field_hs_elna_location_latlon.longitude');
  const addrLine = get(school, 'address.field_hs_elna_address_line1');
  const addr = getAddress(school);
  if (lat && lon) {
    return `${lat},${lon}`;
  }
  if (addrLine) {
    return addr;
  }
  return '';
}

const getImage = (loc) => {
  if (loc) {
    return (
      <Grid.Column styleName="map-grid" floated="right" data-cy="visit-school-detail-map">
        <Image
          alt=""
          src={`https://maps.googleapis.com/maps/api/staticmap?center=${loc}&size=360x360&zoom=15&key=${process.env.GOOGLE_API_KEY}`}
        />
        <Icon alt="" name="map marker alternate" size="big" styleName="map-icon" />
      </Grid.Column>
    );
  } else {
    return null;
  }
};

const CONTACT_TYPE_FAIRS = 'fairs';
const CONTACT_TYPE_VISITS = 'visits';

class HighSchoolDetailsDisplay extends React.PureComponent {
  static propTypes = {
    highSchoolBySchoolId: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      address: PropTypes.object,
      general: PropTypes.object,
    }),
    isNonRepVisits: PropTypes.bool,
    hideMap: PropTypes.bool,
    contactType: PropTypes.string,
    detailsAction: PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }),
    children: PropTypes.arrayOf(PropTypes.object),
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    viewer: PropTypes.shape({
      enabledFeatures: PropTypes.array,
    }),
  };

  getPrimaryContactName(highSchool, defaultContactName) {
    const { contactType } = this.props;

    let primaryContactName;
    switch (contactType) {
      case CONTACT_TYPE_FAIRS:
        primaryContactName = get(highSchool, 'repVisitsAccount.collegeFairsPrimaryContact.name', defaultContactName);
        break;
      case CONTACT_TYPE_VISITS:
        primaryContactName = get(highSchool, 'repVisitsAccount.primaryContact.name', defaultContactName);
        break;
      default:
        primaryContactName = get(highSchool, 'repVisitsAccount.primaryContact.name', defaultContactName);
        break;
    }

    return primaryContactName;
  }

  getPrimaryContactEmail(highSchool) {
    const { contactType } = this.props;

    let primaryContactEmail;
    switch (contactType) {
      case CONTACT_TYPE_FAIRS:
        primaryContactEmail = get(highSchool, 'repVisitsAccount.collegeFairsPrimaryContact.mail');
        break;
      case CONTACT_TYPE_VISITS:
        primaryContactEmail = get(highSchool, 'repVisitsAccount.primaryContact.mail');
        break;
      default:
        primaryContactEmail = get(highSchool, 'repVisitsAccount.primaryContact.mail');
        break;
    }

    return primaryContactEmail;
  }

  render() {
    const { highSchoolBySchoolId, hideMap, detailsAction, children, isNonRepVisits, viewer } = this.props;
    const highSchoolName = get(highSchoolBySchoolId, 'name');
    const isCommunityServerlessOn = viewer?.enabledFeatures?.includes('COMMUNITYSERVERLESS');

    const addr = getAddress(highSchoolBySchoolId);
    const globalId = highSchoolBySchoolId.id && fromGlobalId(highSchoolBySchoolId.id).id;
    const map = hideMap ? null : getImage(getLoc(highSchoolBySchoolId));

    const styleSize = map ? 'details-and-map' : 'details-only';

    const primaryContactEmail = this.getPrimaryContactEmail(highSchoolBySchoolId);
    const primaryContactName = this.getPrimaryContactName(highSchoolBySchoolId, primaryContactEmail);
    const contactDisplay = !primaryContactEmail ? <FormattedMessage id="no.contact" defaultMessage="No Contact" /> :
    <a href={`mailto:${primaryContactEmail}`}> {primaryContactName} <Icon alt="" name="mail outline" /></a>;

    const detailsActionButton = detailsAction && detailsAction.label && (
      <Button
        size="small"
        type="button"
        onClick={detailsAction.onClick}
        disabled={!detailsAction.onClick}
      >
        {detailsAction.label}
      </Button>
    );

    const type = get(highSchoolBySchoolId, 'general.field_hs_elna_school_type', 'Unkown Type');
    const seniorClassSize = get(highSchoolBySchoolId, 'enrollmentByGrade.field_hs_el_grade_12_students', 'Unkown Class Size');
    const collegeGoingRate = get(highSchoolBySchoolId, 'naviance.field_hs_na_percent_college', 'Unkown College Rate');
    return (
      <div id="main-container" styleName={styleSize}>
        <div styleName="banner">
          {detailsActionButton}
          <Header as="h2" styleName="details-header" content="">
            <FormattedMessage {...messages.details} /> {highSchoolName}
          </Header>
        </div>
        <Header as="h3" styleName="details-sub-header" data-cy="repvisits-school-detail-subheader">
          {isNonRepVisits && <FormattedMessage {...messages.nonRepVisits} />}
          {!isNonRepVisits && <FormattedMessage {...messages.HScontact} />}
          {!isNonRepVisits && contactDisplay}
        </Header>
        <Grid>
          <Grid.Column styleName="details-grid" data-cy="repvisit-school-detail-infomation">
            <div>
              <DetailDisplayRow
                label={this.props.intl.formatMessage(messages.address)}
                data={addr}
              />
              <DetailDisplayRow
                label={this.props.intl.formatMessage(messages.phone)}
                data={highSchoolBySchoolId}
                dataPath="naviance.field_hs_elna_phone"
              />
              <DetailDisplayRow
                label={this.props.intl.formatMessage(messages.district)}
                data={get(highSchoolBySchoolId, 'naviance.field_hs_na_district_title', this.props.intl.formatMessage(messages.noDistrict))}
              />
              <DetailDisplayRow
                label={this.props.intl.formatMessage(messages.type)}
                data={type}
              />
              <DetailDisplayRow
                label={this.props.intl.formatMessage(messages.seniorClassSize)}
                data={seniorClassSize}
              />
              <DetailDisplayRow
                label={this.props.intl.formatMessage(messages.collegeGoingRate)}
                data={`${collegeGoingRate}%`}
              />
            </div>
            <br /> <FormattedMessage {...messages.moreInformation} /> <br />
            {
              isCommunityServerlessOn ? (
                <Link to={`/counselorcommunity/search/institutions/highschools/${globalId}`}>
                  {highSchoolName}<FormattedMessage {...messages.counselorCommunity} />
                </Link>
              ) : (
                <a href={`/counselor-community/institution-hs-id/${globalId}`}>
                  {highSchoolName}<FormattedMessage {...messages.counselorCommunity} />
                </a>
              )
            }
          </Grid.Column>
          {map}
        </Grid>
        {children}
      </div>
    );
  }
}

export default injectIntl(HighSchoolDetailsDisplay);
